import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { BrandLogo } from '../BrandLogo';
import {
    LayoutDashboard,
    BookOpen,
    FileText,
    Calculator,
    GraduationCap,
    BarChart3,
    Settings,
    LogOut,
    Menu,
    X,
    ChevronLeft,
    ChevronRight
} from 'lucide-react';

export const Sidebar = ({ isOpen, isCollapsed = false, onClose, onToggleCollapse }) => {
    const { user, logout } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const handleNavClick = (event, link) => {
        if (link.to === '/workspace' && location.pathname === '/workspace') {
            event.preventDefault();
            navigate(`/workspace?tab=home&reset=${Date.now()}`);
            onClose?.();
            return;
        }
        onClose?.();
    };

    const instructorLinks = [
        { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
        { to: '/courses', icon: BookOpen, label: 'Courses' },
        { to: '/assignments', icon: FileText, label: 'Assignments' },
        { to: '/workspace', icon: Calculator, label: 'Accounting Workspace' },
        { to: '/gradebook', icon: GraduationCap, label: 'Gradebook' },
        { to: '/analytics', icon: BarChart3, label: 'Analytics' },
    ];

    const studentLinks = [
        { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
        { to: '/courses', icon: BookOpen, label: 'My Courses' },
        { to: '/assignments', icon: FileText, label: 'Assignments' },
        { to: '/workspace', icon: Calculator, label: 'Accounting Workspace' },
        { to: '/gradebook', icon: GraduationCap, label: 'My Grades' },
    ];

    const adminLinks = [
        { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
        { to: '/admin', icon: Settings, label: 'Admin Console' },
        { to: '/courses', icon: BookOpen, label: 'Courses' },
        { to: '/assignments', icon: FileText, label: 'Assignments' },
        { to: '/workspace', icon: Calculator, label: 'Accounting Workspace' },
        { to: '/gradebook', icon: GraduationCap, label: 'Gradebook' },
        { to: '/analytics', icon: BarChart3, label: 'Analytics' },
    ];

    const auditorLinks = [
        { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
        { to: '/workspace', icon: Calculator, label: 'Accounting Workspace' },
    ];

    const links = user?.role === 'admin' ? adminLinks : user?.role === 'auditor' ? auditorLinks : user?.role === 'instructor' ? instructorLinks : studentLinks;

    return (
        <>
            {/* Mobile overlay */}
            {isOpen && (
                <div
                    className="sidebar-overlay md:hidden"
                    onClick={onClose}
                    data-testid="sidebar-overlay"
                />
            )}

            {/* Sidebar */}
            <aside
                className={`sidebar ${isOpen ? 'open' : ''} ${isCollapsed ? 'collapsed' : ''}`}
                data-testid="sidebar"
                data-collapsed={isCollapsed}
                data-role={user?.role}
            >
                {/* Logo */}
                <div className={`sidebar-header p-4 md:p-6 border-b border-white/10 ${isCollapsed ? 'md:px-0' : ''}`}>
                    <div className={`flex items-center justify-between ${isCollapsed ? 'md:flex-col md:gap-3' : ''}`}>
                        <BrandLogo
                            variant={isCollapsed ? 'mark' : 'wordmark'}
                            className={`sidebar-brand-logo-card ${isCollapsed ? 'is-collapsed md:mx-auto' : ''}`}
                            imgClassName={isCollapsed ? 'sidebar-brand-logo-mark' : 'sidebar-brand-logo-wordmark'}
                            showSubtitle={!isCollapsed}
                            subtitle="Accounting System"
                            subtitleClassName="sidebar-brand-subtitle"
                        />
                        <button
                            className="sidebar-toggle hidden md:flex p-2 hover:bg-white/10 rounded-lg"
                            onClick={onToggleCollapse}
                            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                            data-testid="sidebar-collapse-btn"
                        >
                            {isCollapsed ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
                        </button>
                        <button
                            className="md:hidden p-2 hover:bg-white/10 rounded-lg"
                            onClick={(event) => handleNavClick(event, link)}
                            data-testid="sidebar-close-btn"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* User info */}
                <div className={`sidebar-user-card p-4 mx-4 mt-4 rounded-xl bg-white/5 ${isCollapsed ? 'md:mx-auto md:px-0 md:py-0' : ''}`}>
                    <div className={`flex items-center gap-3 ${isCollapsed ? 'md:justify-center' : ''}`}>
                        <div className="sidebar-avatar w-10 h-10 rounded-full bg-[#2F8FCB] flex items-center justify-center text-sm font-semibold flex-shrink-0">
                            {user?.full_name?.charAt(0).toUpperCase()}
                        </div>
                        <div className={`flex-1 min-w-0 ${isCollapsed ? 'md:hidden' : ''}`}>
                            <p className="text-sm font-medium truncate">{user?.full_name}</p>
                            <p className="text-xs text-white/60 capitalize">{user?.role}</p>
                            {user?.school_name && (
                                <p className="text-[10px] text-[#2F8FCB] truncate" data-testid="sidebar-school-name">{user.school_name}</p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Navigation */}
                <nav className="sidebar-nav flex-1 p-4 space-y-1 overflow-y-auto">
                    {links.map((link) => (
                        <NavLink
                            key={link.to}
                            to={link.to}
                            onClick={onClose}
                            title={link.label}
                            aria-label={link.label}
                            className={({ isActive }) =>
                                `sidebar-item flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium ${isCollapsed ? 'md:justify-center md:px-3' : ''} ${
                                    isActive ? 'is-active' : ''
                                }`
                            }
                            data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
                        >
                            <link.icon className="w-5 h-5 flex-shrink-0" />
                            <span className={isCollapsed ? 'md:hidden' : ''}>{link.label}</span>
                        </NavLink>
                    ))}
                </nav>

                {/* Bottom section */}
                <div className="sidebar-footer p-4 border-t border-white/10">
                    <button
                        onClick={handleLogout}
                        className={`sidebar-item flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium w-full text-left hover:bg-red-500/20 hover:text-red-300 ${isCollapsed ? 'md:justify-center md:px-3' : ''}`}
                        aria-label="Logout"
                        title="Logout"
                        data-testid="logout-btn"
                    >
                        <LogOut className="w-5 h-5 flex-shrink-0" />
                        <span className={isCollapsed ? 'md:hidden' : ''}>Logout</span>
                    </button>
                </div>
            </aside>
        </>
    );
};

export const MobileMenuButton = ({ onClick }) => (
    <button
        className="md:hidden p-2 rounded-lg hover:bg-slate-100"
        onClick={onClick}
        data-testid="mobile-menu-btn"
    >
        <Menu className="w-6 h-6 text-slate-700" />
    </button>
);
